import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    FormGroup, FormControlLabel, Checkbox, Typography, Box, Alert,
    IconButton, Tooltip, CircularProgress, Stack, Divider, ThemeProvider,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import api from '../../services/api';
import { useNotify } from '../../components/Datatable/NotifyProvider';
import lightTheme from '../../themes/lightTheme';

const SCOPE_GROUPS = [
    {
        title: 'DNS Blacklist (listas + domínios)',
        scopes: [
            { id: 'dns_blacklist:read',  label: 'Read' },
            { id: 'dns_blacklist:write', label: 'Write' },
        ],
    },
    {
        title: 'DNS ACL (classes permitidas)',
        scopes: [
            { id: 'dns_acl:read',  label: 'Read' },
            { id: 'dns_acl:write', label: 'Write' },
        ],
    },
    {
        title: 'DNS Local Zone',
        scopes: [
            { id: 'dns_localzone:read',  label: 'Read' },
            { id: 'dns_localzone:write', label: 'Write' },
        ],
    },
    {
        title: 'DNS Nodes (status)',
        scopes: [
            { id: 'dns_node:read', label: 'Read' },
        ],
    },
];

const DEFAULTS = ['dns_blacklist:read', 'dns_blacklist:write'];

export default function CreateTokenDialog({ open, onClose, onCreated }) {
    const notify = useNotify();
    const [name, setName] = useState('');
    const [scopes, setScopes] = useState(DEFAULTS);
    const [expiresAt, setExpiresAt] = useState('');
    const [saving, setSaving] = useState(false);
    const [created, setCreated] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (open) {
            setName('');
            setScopes(DEFAULTS);
            setExpiresAt('');
            setCreated(null);
            setCopied(false);
        }
    }, [open]);

    const toggleScope = (id) => {
        setScopes((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
    };

    const submit = async () => {
        if (!name.trim()) {
            notify.setNotify({ open: true, message: 'Informe um nome', severity: 'warning' });
            return;
        }
        if (scopes.length === 0) {
            notify.setNotify({ open: true, message: 'Selecione pelo menos 1 scope', severity: 'warning' });
            return;
        }
        setSaving(true);
        try {
            const body = { name: name.trim(), scopes };
            if (expiresAt) body.expires_at = expiresAt;
            const res = await api.post('/api-tokens/create', body);
            setCreated(res.data);
        } catch (e) {
            notify.setNotify({
                open: true,
                message: e.response?.data?.detail || 'Erro ao criar token',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const copy = () => {
        navigator.clipboard?.writeText(created.token_raw);
        setCopied(true);
        notify.setNotify({ open: true, message: 'Token copiado', severity: 'success' });
        setTimeout(() => setCopied(false), 2000);
    };

    const finish = () => {
        onCreated();
    };

    return (
        <ThemeProvider theme={lightTheme}>
        <Dialog open={open} onClose={created ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{created ? 'Token criado' : 'Novo token API'}</DialogTitle>
            <DialogContent dividers>
                {!created ? (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Nome"
                            placeholder="ex.: feed-certbr, script-fraude"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={saving}
                            fullWidth
                            autoFocus
                        />

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Permissões (scopes)
                            </Typography>
                            {SCOPE_GROUPS.map((group) => (
                                <Box key={group.title} sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                        {group.title}
                                    </Typography>
                                    <FormGroup row sx={{ gap: 0.5 }}>
                                        {group.scopes.map((s) => (
                                            <FormControlLabel
                                                key={s.id}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={scopes.includes(s.id)}
                                                        onChange={() => toggleScope(s.id)}
                                                        disabled={saving}
                                                    />
                                                }
                                                label={<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{s.id}</Typography>}
                                            />
                                        ))}
                                    </FormGroup>
                                </Box>
                            ))}
                        </Box>

                        <TextField
                            label="Expira em (opcional)"
                            type="datetime-local"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                            disabled={saving}
                            InputLabelProps={{ shrink: true }}
                            helperText="Deixe vazio pra não expirar"
                            fullWidth
                        />
                    </Stack>
                ) : (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="warning">
                            <strong>Salve agora!</strong> O token raw aparece apenas uma vez.
                            Depois disso, só o hash fica armazenado e é impossível recuperar.
                        </Alert>

                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                Token raw
                            </Typography>
                            <Box sx={{
                                p: 1.5,
                                bgcolor: 'grey.100',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                fontFamily: 'monospace',
                                fontSize: 13,
                                wordBreak: 'break-all',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}>
                                <Box sx={{ flexGrow: 1 }}>{created.token_raw}</Box>
                                <Tooltip title={copied ? 'Copiado!' : 'Copiar'}>
                                    <IconButton size="small" onClick={copy}>
                                        {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        <Divider />

                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Uso: <code>Authorization: Token &lt;raw&gt;</code>
                        </Typography>

                        <Box sx={{
                            p: 1.5,
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: 11,
                            whiteSpace: 'pre',
                            overflowX: 'auto',
                        }}>{`curl -X POST https://<host>/api/v1/feed/lists \\
  -H "Authorization: Token ${created.token_raw}" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"feed-test","domains":["evil.com"]}'`}</Box>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {!created ? (
                    <>
                        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
                        <Button variant="contained" onClick={submit} disabled={saving || !name.trim()}>
                            {saving ? <CircularProgress size={18} /> : 'Criar'}
                        </Button>
                    </>
                ) : (
                    <Button variant="contained" onClick={finish}>Pronto</Button>
                )}
            </DialogActions>
        </Dialog>
        </ThemeProvider>
    );
}
